// PATH: src/context/AdminAuthContext.jsx
// VERSION: 2.0 — Primero texto plano; si no hay, usa bcrypt; sesión con expiración

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import bcrypt from 'bcryptjs';
import ENV from '../config/adminEnv';

const AdminAuthContext = createContext(null);
const STORAGE_KEY = 'ADMIN_SESSION_V1';

function nowUtc() { return Date.now(); }
function minutes(n) { return n * 60 * 1000; }

function readSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { isAdmin: false };
    const obj = JSON.parse(raw);
    if (!obj?.exp || obj.exp < nowUtc()) {
      localStorage.removeItem(STORAGE_KEY);
      return { isAdmin: false };
    }
    return { isAdmin: true, exp: obj.exp };
  } catch {
    return { isAdmin: false };
  }
}

function writeSession(minutesFromNow) {
  const exp = nowUtc() + minutes(minutesFromNow);
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ exp }));
  return { isAdmin: true, exp };
}

export function AdminAuthProvider({ children }) {
  const [state, setState] = useState(() => readSession());

  // Auto-expiración simple (chequeo cada minuto)
  useEffect(() => {
    const id = setInterval(() => {
      const cur = readSession();
      if (!cur.isAdmin) setState(cur);
    }, 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const value = useMemo(() => ({
    isAdmin: !!state.isAdmin,

    async login(password) {
      // 1) BYPASS: entra sin contraseña (no recomendado en prod)
      if (ENV.ADMIN_BYPASS) {
        const next = writeSession(ENV.ADMIN_SESSION_MINUTES);
        setState(next);
        return { ok: true, message: 'Acceso concedido (bypass habilitado)' };
      }

      const input = (password || '').toString();

      // 2) TEXTO PLANO: si está definida, la usamos y listo
      if (ENV.ADMIN_PASSWORD) {
        const ok = input === ENV.ADMIN_PASSWORD;
        if (!ok) return { ok: false, message: 'Contraseña incorrecta' };
        const next = writeSession(ENV.ADMIN_SESSION_MINUTES);
        setState(next);
        return { ok: true, message: 'Acceso de administrador concedido' };
      }

      // 3) BCRYPT: si no hay texto plano, usamos hash bcrypt
      const hash = ENV.ADMIN_BCRYPT_HASH;
      if (hash) {
        try {
          const ok = await bcrypt.compare(input, hash);
          if (!ok) return { ok: false, message: 'Contraseña incorrecta' };
          const next = writeSession(ENV.ADMIN_SESSION_MINUTES);
          setState(next);
          return { ok: true, message: 'Acceso de administrador concedido' };
        } catch {
          return { ok: false, message: 'Error validando contraseña' };
        }
      }

      // 4) Sin config
      return { ok: false, message: 'No hay contraseña configurada (define REACT_APP_ADMIN_PASSWORD o un hash).' };
    },

    logout() {
      localStorage.removeItem(STORAGE_KEY);
      setState({ isAdmin: false });
    },
  }), [state.isAdmin]);

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  return useContext(AdminAuthContext);
}
